import {MIN_SUGGESTED_AD_AUDIENCE_SIZE} from '~/global/constants';
import {hasMetaAds, hasCampaignReporting} from '~/utils/featureFlags';
import {apiFetch} from '~/composables/api/apiFetch';
import {useOAuthConfig} from '~/composables/useOAuthConfig';

export type IntegrationConfig = {
  slug: string;
  name: string;
  icon: string;
  oauthUrl?: string;
  connection_slug?: string; // Alternate slug for connection checks
  shortDescription: string;
  longDescription: string;
  isStatic?: boolean;
  isBeta?: boolean;
  hideToggle?: boolean;
  isHidden?: boolean;
  isDeprecated?: boolean;
  deprecatedReason?: string;
  isDownload?: boolean;
  showInput?: boolean;
  requiredProduct?: string[];
  integration_source?: string;
  requiredAudienceSize?: number; // These are for ad audience sizes, we should rename for clarity
  suggestedAudienceSize?: number; // These are for ad audience sizes, we should rename for clarity
  minSuggestedAudienceSize?: number; // These are for OUR audience sizes. These are suggestions. Warnings only.
  maxAudienceSize?: number; // These are for OUR audience sizes
  isNotIntegration?: boolean;
  inputConfig?: {
    type: string;
    max: number;
    min: number;
    defaultValue: number;
    prefixText: string;
    suffixText: string;
  };
  download?: (_segmentId: number | string, _dayCount: number, _allVersions?: boolean) => Promise<void>;
  isComingSoon?: () => Promise<boolean>;
  generateOauthClient?:
    | ((_code: string) => Promise<void>)
    | ((_oauthToken: string, _oauthVerifier: string) => Promise<void>);
  getOauthUrl?: (_options?: {provider?: 'microsoft' | 'google'}) => Promise<string>;
};

const INTEGRATION_ICONS = {
  slack: '/integrations/slack.svg',
  clay: '/integrations/clay.svg',
  hockey_stack: '/integrations/hockey_stack.png',
  hubspot: '/integrations/hubspot.svg',
  webhook: '/integrations/webhook.svg',
  tapistro: '/integrations/tapistro.png',
  persana: '/integrations/persana.png',
  salesforce: '/integrations/salesforce.svg',
  marketo: '/integrations/marketo.svg',
  pardot: '/integrations/salesforce.svg',
  linkedin_ads: '/integrations/linkedin.png',
  linkedin: '/integrations/linkedin.png',
  google_ads: '/integrations/google_ads.svg',
  meta_ads: '/integrations/meta.svg',
  reddit_ads: '/integrations/reddit.svg',
  snapchat_ads: '/integrations/snapchat.svg',
  tiktok_ads: '/integrations/tiktok.svg',
  x_ads: '/integrations/x.png',
  bing_ads: '/integrations/bing.png',
  csv: '/integrations/csv.svg',
} satisfies Record<string, string>;

const INTEGRATION_CONNECTION_SLUG_MAP = {
  linkedin_ads: 'linkedin',
} satisfies Record<string, string>;

export const resolveIntegrationConnectionSlug = (slug: string): string => {
  return slug in INTEGRATION_CONNECTION_SLUG_MAP
    ? INTEGRATION_CONNECTION_SLUG_MAP[slug as keyof typeof INTEGRATION_CONNECTION_SLUG_MAP]
    : slug;
};

export const buildIntegrationConnectionPath = (slug: string): string => {
  const normalizedSlug = resolveIntegrationConnectionSlug(slug.trim());
  if (!normalizedSlug) {
    throw new Error('Integration connection slug is required');
  }

  return `/oauth/${encodeURIComponent(normalizedSlug)}/connection`;
};

export const getIntegrations = async (): Promise<IntegrationConfig[]> => {
  const metaAdsEnabled = await hasMetaAds();
  const campaignReportingEnabled = await hasCampaignReporting();
  const oauth = useOAuthConfig();

  return [
    {
      slug: 'slack',
      name: 'Slack',
      icon: INTEGRATION_ICONS.slack,
      oauthUrl: oauth.buildAuthUrl(oauth.slack),
      shortDescription: 'Connect to Slack to send important segment updates to your team.',
      longDescription: 'Connect to Slack to send important segment updates to your team.',
      minSuggestedAudienceSize: 30 * 1,
      maxAudienceSize: 30 * 250,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/slack/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.slack.redirectUri,
          },
        });
      },
    },
    {
      slug: 'clay',
      name: 'Clay',
      icon: INTEGRATION_ICONS.clay,
      shortDescription: 'Power your tables with contact-level intent',
      longDescription:
        'Power your sales and marketing tables with contact-level intent and website de-anonymization',
      minSuggestedAudienceSize: 30 * 1,
      isComingSoon: async () => {
        return false;
      },
      isStatic: true,
      isHidden: true,
      hideToggle: true,
    },
    {
      slug: 'hockey_stack',
      name: 'HockeyStack',
      icon: INTEGRATION_ICONS.hockey_stack,
      shortDescription: 'Power your analytics with contact-level intent',
      longDescription:
        'Power your analytics with contact-level intent and website de-anonymization',
      isComingSoon: async () => {
        return false;
      },
      isStatic: true,
      isHidden: true,
      hideToggle: true,
    },
    {
      slug: 'hubspot',
      name: 'HubSpot',
      icon: INTEGRATION_ICONS.hubspot,
      oauthUrl: oauth.buildAuthUrl(oauth.hubspot),
      shortDescription: 'Sync accounts and activities from specific segments to your CRM.',
      longDescription:
        "HubSpot is a customer relationship management (CRM) platform that provides software and support to help businesses grow better. The platform includes marketing, sales, service, and website management products that start free and scale to meet our customers' needs at any stage of growth.",
      requiredProduct: ['reveal', 'target'],
      minSuggestedAudienceSize: 30 * 1,
      maxAudienceSize: 30 * 1500,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/hubspot/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.hubspot.redirectUri,
          },
        });
      },
    },
    {
      slug: 'webhook',
      name: 'Webhook',
      icon: INTEGRATION_ICONS.webhook,
      shortDescription: 'Trigger any workflow from a specific segment with a webhook.',
      longDescription: 'Trigger any workflow from a specific segment with a webhook.',
      isComingSoon: async () => {
        return false;
      },
    },
    {
      slug: 'tapistro',
      name: 'Tapistro',
      icon: INTEGRATION_ICONS.tapistro,
      shortDescription: 'Accelerate revenue with AI-native buyer signal orchestration',
      longDescription: 'Accelerate revenue with AI-native buyer signal orchestration',
      isComingSoon: async () => {
        return false;
      },
      isStatic: true,
      isHidden: true,
      hideToggle: true,
    },
    {
      slug: 'persana',
      name: 'Persana',
      icon: INTEGRATION_ICONS.persana,
      shortDescription: 'Turn anonymous visitors into personalize outreach at scale',
      longDescription: 'Personalized Outreach with Better Data at Scale',
      isComingSoon: async () => {
        return false;
      },
      isStatic: true,
      isHidden: true,
      hideToggle: true,
    },
    {
      slug: 'salesforce',
      name: 'Salesforce',
      icon: INTEGRATION_ICONS.salesforce,
      oauthUrl: oauth.buildAuthUrl(oauth.salesforce),
      shortDescription: 'Sync accounts and activities from specific segments to your CRM.',
      longDescription:
        'Salesforce is a customer relationship management solution that brings companies and customers together. It’s one integrated CRM platform that gives all your departments — including marketing, sales, commerce, and service — a single, shared view of every customer.',
      requiredProduct: ['reveal', 'target'],
      minSuggestedAudienceSize: 30 * 1,
      maxAudienceSize: 30 * 1500,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/salesforce/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.salesforce.redirectUri,
          },
        });
      },
    },
    {
      slug: 'marketo',
      name: 'Marketo',
      icon: INTEGRATION_ICONS.marketo,
      oauthUrl: oauth.buildAuthUrl(oauth.salesforce),
      shortDescription: 'Sync your accounts and create activities in your MAP',
      longDescription:
        'Salesforce is a customer relationship management solution that brings companies and customers together. It’s one integrated CRM platform that gives all your departments — including marketing, sales, commerce, and service — a single, shared view of every customer.',
      requiredProduct: ['reveal', 'target'],
      minSuggestedAudienceSize: 30 * 1,
      maxAudienceSize: 30 * 1500,
      isHidden: true,
      isComingSoon: async () => {
        return true;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/salesforce/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.salesforce.redirectUri,
          },
        });
      },
    },
    {
      slug: 'pardot',
      name: 'Pardot',
      icon: INTEGRATION_ICONS.pardot,
      oauthUrl: oauth.buildAuthUrl(oauth.pardot),
      shortDescription: 'Sync accounts and activities from specific segments to your MAP.',
      longDescription:
        'Pardot is a marketing automation platform by Salesforce that helps businesses engage with customers, generate leads, and drive sales through personalized and automated marketing campaigns. It empowers marketing teams with tools for email marketing, lead nurturing, prospect tracking, and ROI reporting, providing a seamless connection between marketing and sales to ensure a unified customer experience.',
      requiredProduct: ['reveal', 'target'],
      minSuggestedAudienceSize: 30 * 1,
      maxAudienceSize: 30 * 1500,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/pardot/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.pardot.redirectUri,
          },
        });
      },
    },
    {
      slug: 'linkedin_ads',
      name: 'LinkedIn Ads',
      icon: INTEGRATION_ICONS.linkedin_ads,
      shortDescription: 'Send high value contacts to dynamic audiences for LinkedIn Ads',
      longDescription:
        "Using LinkedIn Ads to reach your contacts allows you to engage decision-makers and professionals where they are most active in a business context. By targeting your contacts with tailored campaigns on LinkedIn, you can increase brand awareness, nurture leads, and drive conversions with highly relevant messaging, all while leveraging LinkedIn's robust targeting and analytics capabilities.",
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      requiredAudienceSize: 300,
      suggestedAudienceSize: 1000,
      isNotIntegration: true,
      connection_slug: INTEGRATION_CONNECTION_SLUG_MAP.linkedin_ads,
    },
    {
      slug: 'linkedin',
      name: 'LinkedIn',
      icon: INTEGRATION_ICONS.linkedin,
      oauthUrl: oauth.buildAuthUrl(oauth.linkedin),
      shortDescription:
        'Sync in campaign reporting data and send high value contacts to dynamic audiences for LinkedIn Ads.',
      longDescription: '',
      requiredProduct: ['target'],
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/linkedin/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.linkedin.redirectUri,
          },
        });
      },
      isHidden: !campaignReportingEnabled,
    },
    {
      slug: 'google_ads',
      name: 'Google Ads',
      icon: INTEGRATION_ICONS.google_ads,
      oauthUrl: oauth.buildAuthUrl(oauth.googleAds, {access_type: 'offline', prompt: 'consent'}),
      shortDescription: 'Send high value contacts to dynamic audiences for Google Ads',
      longDescription:
        'Google Ads is an online advertising platform developed by Google that helps businesses connect with their target audience and drive growth. The platform allows users to create and manage campaigns across search, display, video, and app networks, reaching potential customers wherever they are online. With advanced targeting, performance tracking, and scalable solutions, Google Ads supports businesses of all sizes in achieving their marketing goals.',
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/google_ads/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.googleAds.redirectUri,
          },
        });
      },
      // isStatic: true,
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 1000,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/google_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'meta_ads',
      name: 'Meta Ads',
      icon: INTEGRATION_ICONS.meta_ads,
      oauthUrl: oauth.buildAuthUrl(oauth.metaAds, {
        access_type: 'offline',
        prompt: 'consent',
        config_id: '1338232977712295',
      }),
      shortDescription: 'Send high value contacts to dynamic audiences for Meta Ads',
      longDescription: '',
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/meta_ads/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.metaAds.redirectUri,
          },
        });
      },
      isStatic: metaAdsEnabled ? false : true,
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 1000,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/meta_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'reddit_ads',
      name: 'Reddit Ads',
      icon: INTEGRATION_ICONS.reddit_ads,
      oauthUrl: oauth.buildAuthUrl(oauth.redditAds, {state: 'vector', duration: 'permanent'}),
      shortDescription: 'Send high value contacts to dynamic audiences for Reddit Ads',
      longDescription: '',
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (code: string) => {
        await apiFetch(`/oauth/reddit_ads/generate-oauth-client`, {
          method: 'POST',
          body: {
            code,
            redirectUri: oauth.redditAds.redirectUri,
          },
        });
      },
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 1000,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/reddit_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'snapchat_ads',
      name: 'Snapchat Ads',
      icon: INTEGRATION_ICONS.snapchat_ads,
      shortDescription: 'Send high value contacts to dynamic audiences for Snapchat Ads',
      longDescription: '',
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      isStatic: true,
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 1000,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/snapchat_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'tiktok_ads',
      name: 'Tiktok Ads',
      icon: INTEGRATION_ICONS.tiktok_ads,
      shortDescription: 'Send high value contacts to dynamic audiences for Tiktok Ads',
      longDescription: '',
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      isStatic: true,
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 1000,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/tiktok_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'x_ads',
      name: 'X Ads',
      icon: INTEGRATION_ICONS.x_ads,
      getOauthUrl: async () => {
        const url = await apiFetch(`/oauth/x_ads/get-oauth-url`);
        return url as string;
      },
      shortDescription: 'Send high value contacts to dynamic audiences for X Ads',
      longDescription: 'Send high value contacts to dynamic audiences for X Ads',
      requiredProduct: ['target'],
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      generateOauthClient: async (oauthToken: string, oauthVerifier: string) => {
        await apiFetch(`/oauth/x_ads/generate-oauth-client`, {
          method: 'POST',
          body: {
            oauthToken,
            oauthVerifier,
            redirectUri: oauth.xAds.redirectUri,
          },
        });
      },
      // isStatic: true,
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 100,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/x_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'bing_ads',
      name: 'Bing Ads',
      icon: INTEGRATION_ICONS.bing_ads,
      isBeta: true,
      requiredProduct: ['target'],
      shortDescription: 'Send high value contacts to dynamic audiences for Bing Ads',
      longDescription:
        'Bing Ads is an online advertising platform developed by Microsoft that helps businesses connect with their target audience and drive growth. The platform allows users to create and manage campaigns across search, display, video, and app networks, reaching potential customers wherever they are online. With advanced targeting, performance tracking, and scalable solutions, Bing Ads supports businesses of all sizes in achieving their marketing goals.',
      minSuggestedAudienceSize: MIN_SUGGESTED_AD_AUDIENCE_SIZE,
      isComingSoon: async () => {
        return false;
      },
      getOauthUrl: async (options: {provider?: 'microsoft' | 'google'} = {}) => {
        const provider = options.provider ?? 'microsoft';
        if (provider === 'google') {
          if (!oauth.bingAdsGoogle.clientId) {
            throw new Error('BING_ADS_GOOGLE_CLIENT_ID is not set');
          }
          return oauth.buildAuthUrl(oauth.bingAdsGoogle, {
            access_type: 'offline',
            prompt: 'consent',
          });
        }
        return oauth.buildAuthUrl(oauth.bingAds);
      },
      generateOauthClient: async (code: string) => {
        const provider =
          sessionStorage.getItem('bingAdsProvider') === 'google' ? 'google' : 'microsoft';
        const oauthConfig = provider === 'google' ? oauth.bingAdsGoogle : oauth.bingAds;
        try {
          await apiFetch(`/oauth/bing_ads/generate-oauth-client`, {
            method: 'POST',
            body: {
              code,
              redirectUri: oauthConfig.redirectUri,
              identityProvider: provider,
            },
          });
        } finally {
          sessionStorage.removeItem('bingAdsProvider');
        }
      },
      hideToggle: true,
      isDownload: true,
      showInput: true,
      requiredAudienceSize: 1000,
      suggestedAudienceSize: 1000,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/bing_ads/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
    {
      slug: 'csv',
      name: 'CSV',
      icon: INTEGRATION_ICONS.csv,
      shortDescription: 'Download data as CSV',
      longDescription: '',
      requiredProduct: ['reveal', 'target', 'api'],
      isComingSoon: async () => {
        return false;
      },
      isNotIntegration: true,
      isStatic: true,
      hideToggle: true,
      isDownload: true,
      showInput: true,
      inputConfig: {
        type: 'number',
        max: 180,
        min: 1,
        defaultValue: 30,
        prefixText: 'Download data for the last',
        suffixText: 'days',
      },
      download: async (segmentId: number | string, dayCount: number, allVersions?: boolean) => {
        const params = allVersions ? '&allVersions=true' : '';
        await apiFetch(`/csv/export/csv?segmentId=${segmentId}&dayCount=${dayCount}${params}`, {});
      },
    },
  ];
};

export const resolveIntegrationIcon = (slug: IntegrationSlug): string => {
  return slug in INTEGRATION_ICONS
    ? INTEGRATION_ICONS[slug as keyof typeof INTEGRATION_ICONS]
    : '/integrations/default.svg';
};

export type IntegrationSlug = Awaited<ReturnType<typeof getIntegrations>>[number]['slug'];
